import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router'
import classNames from 'classnames'

import { getDocFlowProfile } from '../../api'
import { EMPTY_OBJECT, NOTIFICATION_TYPE } from '../../constants/common'
import {
  getPageBrowserTabTitleWithEntityName,
  useAsyncCallback,
  useLayoutOverlayContext,
  usePageBrowserTabTitle,
} from '../../hooks'
import { useAsyncEntity } from '../Entity'
import { NotificationInline } from '../Notification'
import { EntityPageProfileLayout } from '../Page'
import { Skeleton } from '../Skeleton'

import { updateDocFlowProfile } from './DocFlow.api'
import { DOC_FLOW_ACTIVE_PHASE_BY_COPING_FILTER } from './DocFlow.constants'
import {
  getPhaseContent,
  getPhaseNotifications,
  isDocFlowPhaseSplitting,
} from './DocFlow.utils.phases'
import {
  isStorageCombiningPhaseAsync,
  setStorageCombiningPhaseAsyncStatus,
} from './DocFlow.utils.storage'
import { DocFlowProfileHeader } from './DocFlowProfileHeader'
import { DocFlowProvider } from './DocFlowProvider'

import * as styles from './DocFlow.module.css'

function DocFlow() {
  const { t } = useTranslation()

  const { documentId } = useParams()
  const { state } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [modal, setModal] = useState(null)
  const [activePhase, setActivePhase] = useState(null)
  const [prevIsInitiated, setPrevIsInitiated] = useState(undefined)
  const [footerProps, setFooterProps] = useState(EMPTY_OBJECT)
  const [mappingPhaseFileActive, setMappingPhaseFileActive] = useState(null)

  const pageScrollWrapperRef = useRef(null)

  const { showGretaAIChat, showProductFruits, withModalsTray } =
    useLayoutOverlayContext()

  function getProfileData() {
    return getDocFlowProfile(documentId)
  }

  function updateProfileData(data) {
    return updateDocFlowProfile(data, documentId)
  }

  const {
    entityData,
    handleEntityReload,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({
    getProfileData,
    updateProfileData,
  })

  usePageBrowserTabTitle({
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: entityData?.name,
      title: t('BrowserTab.DocFlowProfileTabTitleKey'),
    }),
  })

  const asyncActionInProccess = isStorageCombiningPhaseAsync()

  function handleCloseModal() {
    setModal(null)
  }

  // Set the active phase from the copy filter or entity data — during render.
  // `prevIsInitiated` starts as `undefined`, so this runs on mount (matching
  // the original effect) and again whenever `isInitiated` changes.
  if (isInitiated !== prevIsInitiated) {
    setPrevIsInitiated(isInitiated)

    if (state?.table_name) {
      setActivePhase(DOC_FLOW_ACTIVE_PHASE_BY_COPING_FILTER[state.table_name])
    } else if (isInitiated && entityData) {
      setActivePhase(entityData.phase)
    }
  }

  useEffect(() => {
    setStorageCombiningPhaseAsyncStatus(false)
  }, [])

  const activePhaseDisabled =
    activePhase < entityData?.phase || entityData?.archived

  const phaseContent = getPhaseContent(activePhase)

  useEffect(() => {
    if (
      activePhase !== null &&
      !isDocFlowPhaseSplitting(activePhase) &&
      searchParams.size > 0
    ) {
      setSearchParams(EMPTY_OBJECT, { replace: true })
    }
  }, [activePhase])

  const phaseNotifications = getPhaseNotifications(activePhase)

  const handleNextPhase = useAsyncCallback(() => {
    return handleUpdateEntity({ phase: entityData?.phase + 1 }, null).then(
      data => {
        setActivePhase(data.phase)
        handleCloseModal()

        return data
      }
    )
  }, [entityData?.phase, handleCloseModal, handleUpdateEntity, setActivePhase])

  return (
    <EntityPageProfileLayout
      scrollWrapperClassName={classNames(styles.scrollWrapper, {
        [styles.withModalsTray]: withModalsTray,
        [styles.withSupportButtons]: showGretaAIChat || showProductFruits,
      })}
      scrollWrapperRef={pageScrollWrapperRef}
      serverError={serverError}
    >
      <DocFlowProvider
        activePhase={activePhase}
        asyncActionInProccess={asyncActionInProccess}
        containerRef={pageScrollWrapperRef}
        docFlow={entityData}
        docFlowInitiated={isInitiated}
        documentId={documentId}
        footerProps={footerProps}
        handleCloseModal={handleCloseModal}
        handleNextPhase={handleNextPhase}
        handleReloadDocFlow={handleEntityReload}
        handleUpdateDocFlow={handleUpdateEntity}
        isDocFlowLoading={isLoading}
        mappingPhaseFileActive={mappingPhaseFileActive}
        phaseDisabled={activePhaseDisabled}
        setActivePhase={setActivePhase}
        setFooterProps={setFooterProps}
        setMappingPhaseFileActive={setMappingPhaseFileActive}
        setModal={setModal}
      >
        <DocFlowProfileHeader />

        {!isInitiated ? (
          <Skeleton className={styles.notificationSkeleton} />
        ) : (
          <div className={styles.notifications}>
            {phaseNotifications.map(item => (
              <NotificationInline
                className={styles.notification}
                key={item.text}
                text={t(item.text)}
                type={item.type || NOTIFICATION_TYPE.info}
              />
            ))}
          </div>
        )}

        {isInitiated ? phaseContent : null}

        {modal}
      </DocFlowProvider>
    </EntityPageProfileLayout>
  )
}

export default DocFlow
